<template>
    <div class="container-fluid form-container fire-alarm-test-form-container">
        <div class="row">
            <div class="col-sm-12">
                <h1 class="page-title">
                    Fire Alarm Commissioning
                </h1>
            </div>
        </div>
        <form
            action=""
            class="fire-alarm-certificate"
            @submit.prevent="saveForm()"
        >
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="customer">Customer</label>
                            <customer-search
                                ref="customerSearch"
                                :customer="form.customer"
                                @change="customerChanged"
                                :customer-id="form.customer_id"
                            />
                            <error
                                name="customer_id"
                                :errors="errors"
                            />
                        </div>
                        <div
                            class="col-sm-12 col-md-6 form-group"
                            v-if="form.customer && form.customer.sites"
                        >
                            <label for="site">Site:</label>
                            <select
                                name="site"
                                v-model="form.site_id"
                                id="site"
                                class="form-control"
                                placeholder="Site"
                                required
                                v-if="form.customer.sites.length"
                                @change="siteChanged()"
                            >
                                <option
                                    v-for="site in form.customer.sites"
                                    :key="site.id"
                                    :value="site.id"
                                >
                                    #{{ site.site_no }} {{ site.name }}
                                </option>
                            </select>
                            <span
                                class="form-error"
                                v-if="!form.customer.sites.length"
                            >This customer has no sites</span>
                            <error
                                name="site_id"
                                :errors="errors"
                            />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-12 col-md-3 form-group">
                            <label for="job_id">Job No.</label>
                            <select
                                v-if="jobs"
                                name="job_id"
                                id="job_id"
                                v-model="form.job_id"
                                class="form-control"
                                placeholder="Select a job"
                                :disabled="jobs.length < 1"
                            >
                                <option
                                    :value="null"
                                    disabled
                                >
                                    Select a job
                                </option>
                                <option
                                    v-for="job in jobs"
                                    :key="job.id"
                                    :value="job.id"
                                >
                                    {{ job.id }}: {{ job.name }}
                                </option>
                            </select>
                            <error
                                name="job_id"
                                :errors="errors"
                            />
                        </div>

                        <div class="col-sm-12 col-md-3 form-group">
                            <label for="doc_no">Document No.</label>
                            <input
                                type="text"
                                id="doc_no"
                                name="doc_no"
                                v-model="form.doc_no"
                                placeholder="Document Number"
                                class="form-control"
                            >
                            <error
                                name="doc_no"
                                :errors="errors"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <select-row-component
                        id="installed_by_others"
                        label="Was the installation work carried out by others?"
                        v-model="form.installed_by_others"
                        required
                    />

                    <select-row-component
                        id="installed_by_others"
                        label="If so, what is the standard of the installation?"
                        v-model="form.installation_standard"
                        :required="form.installed_by_others === 'Yes'"
                        :options="['Good', 'Fair', 'Poor', 'N/A']"
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <h3>Details of checks carried out</h3>
                        </div>
                    </div>

                    <select-row-component
                        id="design_spec_received"
                        label="1. Design specification received"
                        v-model="form.design_spec_received"
                        include-na
                    />

                    <select-row-component
                        id="as_fitted_drawing"
                        label="2. 'As Fitted' drawing complete/received"
                        v-model="form.as_fitted_drawing"
                        include-na
                    />

                    <select-row-component
                        id="all_control_equipment_recorded"
                        label="3. Details of location of all control equipment recorded"
                        v-model="form.all_control_equipment_recorded"
                        include-na
                    />

                    <select-row-component
                        id="all_devices_recorded"
                        label="4. Details of location of all devices, power supplies PSUs & junction boxes recorded"
                        v-model="form.all_devices_recorded"
                        include-na
                    />

                    <select-row-component
                        id="mains_dist_recorded"
                        label="5. Details of location of mains distribution board recorded"
                        v-model="form.mains_dist_recorded"
                        include-na
                    />

                    <select-row-component
                        id="cable_type_recorded"
                        label="6. Details of cable type, size & routing recorded"
                        v-model="form.cable_type_recorded"
                        include-na
                    />

                    <select-row-component
                        id="fire_alarm_zones_recorded"
                        label="7. Fire Alarm Zones recorded"
                        v-model="form.fire_alarm_zones_recorded"
                        include-na
                    />

                    <select-row-component
                        id="devices_inspected_tested"
                        label="8. All devices inspected & tested"
                        v-model="form.devices_inspected_tested"
                        include-na
                    />

                    <select-row-component
                        id="cause_and_effect_verified"
                        label="9. Cause & effect verified to design specification"
                        v-model="form.cause_and_effect_verified"
                        include-na
                    />

                    <select-row-component
                        id="batter_calc_verified"
                        label="10. Battery calculations verified to design specification"
                        v-model="form.battery_calc_verified"
                        include-na
                    />

                    <select-row-component
                        id="db_sound_recorded"
                        label="11. Db sound pressure levels recorded"
                        v-model="form.db_sound_recorded"
                        include-na
                    />

                    <select-row-component
                        id="electrical_test_cert"
                        label="12. Electrical Installation Test Certificate completed/received"
                        v-model="form.electrical_test_cert"
                        include-na
                    />

                    <select-row-component
                        id="mains_supply_cert"
                        label="13. Electrical Installation Mains Supply Certificate completed/received"
                        v-model="form.mains_supply_cert"
                        include-na
                    />

                    <select-row-component
                        id="main_supply_labelled"
                        label="14. Main supply clearly labelled"
                        v-model="form.main_supply_labelled"
                        include-na
                    />

                    <select-row-component
                        id="protection_soft_cable"
                        label="15. Mechanical protection provided for up to 2m of soft skin cable"
                        v-model="form.protection_soft_cable"
                        include-na
                    />

                    <select-row-component
                        id="joint_boxes_labelled"
                        label="16. Join-boxes labelled 'Fire Alarm'"
                        v-model="form.joint_boxes_labelled"
                        include-na
                    />

                    <select-row-component
                        id="earthing_bonded"
                        label="17. All Earthing Conductors Bonded"
                        v-model="form.earthing_bonded"
                        include-na
                    />

                    <select-row-component
                        id="main_supply_spur"
                        label="18. Main supply spur is 'Unswitched' or 'Lockable Isolator' (Must be double polled)"
                        v-model="form.main_supply_spur"
                        include-na
                    />

                    <select-row-component
                        id="cable_segregation"
                        label="19. Cable segregation & type meets standard"
                        v-model="form.cable_segregation"
                        include-na
                    />

                    <select-row-component
                        id="sounder_circuits"
                        label="20. Min of 2 sounder circuits or 'Loop powered' sounders used (1st or last device on any)"
                        v-model="form.sounder_circuits"
                        include-na
                    />

                    <select-row-component
                        id="remote_signalling"
                        label="21. Remote signalling operation verified"
                        v-model="form.remote_signalling"
                        include-na
                    />

                    <select-row-component
                        id="zonal_diagram"
                        label="22. Zonal diagram installed"
                        v-model="form.zonal_diagram"
                        include-na
                    />

                    <select-row-component
                        id="log_book_issued"
                        label="23. Log book issued"
                        v-model="form.log_book_issued"
                        include-na
                    />

                    <select-row-component
                        id="operation_manual_supplied"
                        label="24. Operation manual supplied"
                        v-model="form.operation_manual_supplied"
                        include-na
                    />

                    <select-row-component
                        id="record_relay_checks"
                        label="25. Record of interface/relay checks completed (connection & operation)"
                        v-model="form.record_relay_checks"
                        include-na
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <h3>Verification</h3>
                        </div>
                    </div>

                    <select-row-component
                        id="as_fitted_accurately"
                        label="A. Does the 'As fitted' drawing/spec accurately report he structure of the building"
                        v-model="form.as_fitted_accurately"
                        include-na
                    />

                    <select-row-component
                        id="building_use_same"
                        label="B. Is the use of the building at commissioning the sam as intended design"
                        v-model="form.building_use_same"
                        include-na
                    />

                    <select-row-component
                        id="variations_documented"
                        label="C. Have all variations been documented, explained & signed for by the customer"
                        v-model="form.variations_documented"
                        include-na
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <h3>Design Category</h3>
                        </div>
                    </div>

                    <select-row-component
                        id="design_category_level"
                        label="Commission against design category level"
                        v-model="form.design_category_level"
                        :options="fire_alarm_systems"
                        include-na
                    />

                    <select-row-component
                        id="determined_category_level"
                        label="Design category not specified, engineer to determine"
                        v-model="form.determined_category_level"
                        :options="fire_alarm_systems"
                        include-na
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <h3>Battery Calculator</h3>
                        </div>
                    </div>

                    <text-row-component
                        id="battery_q"
                        label="Quiescent current in standby mode (Q) mA"
                        v-model="form.battery_q"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_a"
                        label="Alarm current in alarm mode (A) mA"
                        v-model="form.battery_a"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_standby_hours"
                        label="Standby hours"
                        v-model="form.battery_standby_hours"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_alarm_hours"
                        label="Alarm hours"
                        v-model="form.battery_alarm_hours"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_standby_capacity"
                        label="Standby capacity"
                        v-model="form.battery_standby_capacity"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_alarm_capacity"
                        label="Alarm capacity"
                        v-model="form.battery_alarm_capacity"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_standby_and_alarm"
                        label="Standby + alarm"
                        v-model="form.battery_standby_and_alarm"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <text-row-component
                        id="battery_size"
                        label="Battery size (standby + alarm / 1000 x 1.25) A/hr"
                        v-model="form.battery_size"
                        type="number"
                        min="0"
                        step="0.001"
                    />

                    <div class="row">
                        <div class="col-sm-12 text-right">
                            <button
                                type="button"
                                class="btn btn-sm btn-secondary"
                                @click.prevent="calcBattery"
                            >
                                Calculate Battery
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>Final Checks</h3>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="equipment_operates_correctly"
                                v-model="form.equipment_operates_correctly"
                            >
                            <label
                                class="mb-0"
                                for="equipment_operates_correctly"
                            >
                                All equipment operates correctly
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="installation_acceptable_standard"
                                v-model="form.installation_acceptable_standard"
                            >
                            <label
                                class="mb-0"
                                for="installation_acceptable_standard"
                            >
                                Installation work is, as far as can reasonably be ascertained, of an acceptable standard
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="system_inspected"
                                v-model="form.system_inspected"
                            >
                            <label
                                class="mb-0"
                                for="system_inspected"
                            >
                                The entire system has been inspected and tested in accordance with recommendations of
                                39.2c of BS 5839-1:2017
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="system_performs"
                                v-model="form.system_performs"
                            >
                            <label
                                class="mb-0"
                                for="system_performs"
                            >
                                The system performs as required by the specification prepared by:
                            </label>
                            <input
                                type="text"
                                v-model="form.system_performs_by"
                                class="form-control flex-grow-0 w-auto mx-2"
                            >
                            <label>a copy of which I/we have been given</label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="no_unacceptable_alarm_rate"
                                v-model="form.no_unacceptable_alarm_rate"
                            >
                            <label
                                class="mb-0"
                                for="no_unacceptable_alarm_rate"
                            >
                                Taking into account the guidance contained in Section 3 of BS 5839-1:2017, I/we have not
                                identifies any obvious potential for an unacceptable rate of false alarms.
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="documentation_provided"
                                v-model="form.documentation_provided"
                            >
                            <label
                                class="mb-0"
                                for="documentation_provided"
                            >
                                The documentation described in Clause 40 of BS 5839-1:2013 has been provided to the
                                user.
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="liability_extent">
                                The extent of liability of the signatory is limited to the system described below –
                                Extent of system covered by this certificate:
                            </label>
                            <textarea
                                v-model="form.liability_extent"
                                id="liability_extent"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="variations_text">
                                Variations from the recommendations of clause 39 of (see BS 5839-1:2017, Clause 7)
                            </label>
                            <textarea
                                v-model="form.variations_text"
                                id="variations_text"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="work_to_completed">
                                The following work should be completed before/after (delete as applicable) the system
                                becomes operational
                            </label>
                            <textarea
                                v-model="form.work_to_completed"
                                id="work_to_completed"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="false_alarm_causes">
                                The following potential cause of false alarms should be considered at the time of the
                                next service visit
                            </label>
                            <textarea
                                v-model="form.false_alarm_causes"
                                id="false_alarm_causes"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12">
                            <div class="d-flex align-items-center flex-wrap">
                                <span class="w-auto">
                                    Before the system becomes operational, it should be soak tested in accordance with the recommendations of 35.2.6 of BS 5839-1:2017 for a period of:
                                </span>
                                <input
                                    v-model="form.soak_tested_duration"
                                    id="soak_tested_duration"
                                    class="form-control w-auto flex-grow-0 mx-2"
                                >
                            </div>
                            <span>
                                (Enter a period of either one week, such period as required by the specification, or such period as recommended by the signatory to this certificate, whichever is the greatest, or delete if not applicable.)
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12 col-md-6 d-flex align-items-center">
                            <label
                                for="engineer_id"
                                class="mb-0"
                            >Engineer name:</label>
                            <user-select-component
                                v-model="form.engineer_id"
                                :admins="false"
                                :office="false"
                                class="w-auto ml-1"
                            />
                        </div>

                        <div class="col-12 col-md-6 d-flex align-items-center justify-content-end">
                            <label
                                class="mb-0"
                            >Date:</label>
                            <datetimepicker
                                v-model="form.form_date"
                                :options="{format: 'L'}"
                                class="mb-0 ml-1"
                            />
                        </div>
                    </div>
                </div>
            </div>


            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <button
                                class="btn btn-success"
                                type="submit"
                                :disabled="isLoading || isSaving"
                            >
                                Save
                            </button>

                            <button
                                class="btn btn-outline-danger"
                                type="button"
                                @click="deleteForm()"
                                :disabled="isLoading || isSaving"
                                v-if="form.id && canDelete"
                            >
                                <i class="fa fa-times" /> Delete
                            </button>

                            <a
                                class="btn btn-info"
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/stream'"
                                target="_blank"
                            >
                                <i class="fa fa-print" /> Preview/Print
                            </a>

                            <a
                                class="btn btn-info"
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/download'"
                                target="_blank"
                                :download="form.doc_no + '.pdf'"
                            >
                                <i class="fa fa-file" /> Download PDF
                            </a>

                            <router-link
                                class="btn btn-info"
                                v-if="form.id && form.customer_id"
                                :to="{name: 'office.emails.new', params: {customer_id: form.customer_id}, query: {form_id: form.id}}"
                            >
                                <i class="fa fa-envelope" /> Send to Customer
                            </router-link>

                            <router-link
                                class="btn btn-info"
                                v-if="form.id"
                                :to="{name: 'office.forms.new', query: {commissioning_form: form.id, type: 'fire_alarm_commissioning_certificate'}}"
                            >
                                <i class="fa fa-copy" /> Create Certificate
                            </router-link>

                            <customer-site-footer-links
                                :customer-id="form.customer_id"
                                :site-id="form.site_id"
                            />

                            <activity-log
                                v-if="form.id"
                                :id="form.id"
                                model="Form"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import CustomerSearchComponent from "../CustomerSearch.vue";
import FormService from "../../services/FormService";
import * as moment from "moment";
import AuthService from "../../services/AuthService";
import UsesErrors from "../../mixins/UsesErrors";
import SettingsMixin from "../../mixins/SystemSettings";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";
import UsesFormRouteQuery, { formRouteQueryWatchers } from "../../mixins/UsesFormRouteQuery";
import SelectRowComponent from "./parts/SelectRowComponent.vue";
import TextRowComponent from "./parts/TextRowComponent.vue";
import JobService from "../../services/JobService";
import {fire_alarm_systems} from "../../include/Form";
import UserSelectComponent from "../UserSelectComponent.vue";
import Datetimepicker from "../DateTimePickerComponent.vue";

const initialForm = {
    type: 'fire_alarm_commissioning',
    customer: null,
    form_date: moment(),
};

export default {
    components: {
        Datetimepicker,
        UserSelectComponent,
        TextRowComponent,
        SelectRowComponent,
        "customer-search": CustomerSearchComponent,
    },
    mixins: [
        UsesErrors,
        UsesInfoMessage,
        SettingsMixin,
        UsesFormRouteQuery,
    ],
    data: function () {
        return {
            form: {...initialForm},
            token: null,
            sites: [],
            jobs: [],
            fire_alarm_systems,
        }
    },
    computed: {
        canDelete: function () {
            return AuthService.user.admin_access || AuthService.user.office_access;
        },
        idParam: function () {
            return this.$route.params.id;
        },
    },

    watch: {
        ...formRouteQueryWatchers,
        idParam: {
            handler: function () {
                if (this.idParam) {
                    this.loadForm(this.idParam);
                    return;
                }

                this.form = {...initialForm};
                FormService.nextNo().then(res => {
                    this.form.doc_no = res.data;
                });
            },
            immediate: true,
        },
    },

    mounted: function () {

        if (!this.form.explained_by_name && AuthService.user.engineer_access) {
            this.form.explained_by_name = AuthService.user.name;
        }
    },

    methods: {
        loadForm: function (id) {
            this.setLoading();
            FormService.get(id).then(res => {
                this.form = res.data;

                if (!this.form.customer) {
                    this.form.customer = {
                        company_name: '',
                    };
                }
            }, () => {
                this.setErrorMessage('Could not load form');
            }).then(() => {
                this.clearLoading();
            });
        },

        customerChanged: function (customer) {
            this.form.customer = customer ? customer : {};
            if (this.form.customer) {
                this.form.customer_id = this.form.customer.id;
                this.getJobs();
            } else {
                this.form.customer_id = null;
            }
            if (customer && customer.sites) {
                if (!customer.sites.map(site => site.id).includes(this.form.site_id)) {
                    this.form.site_id = '';
                    this.siteChanged();
                }
            } else {
                this.form.site_id = '';
                this.siteChanged();
            }
        },

        siteChanged: function () {
        },

        saveForm: function () {
            this.setSaving();
            let data = {...this.form};
            delete data['customer'];
            delete data['site'];
            delete data['job'];
            delete data['data'];

            if (this.form.id) {
                FormService.update(data).then(
                    res => {
                        this.clearSaving();
                        this.setInfoMessage("Form saved successfully");
                        this.$emit('formSaved', res.data);
                    },
                    err => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    }
                );
            } else {
                FormService.create(data).then(
                    res => {
                        this.setInfoMessage("Form saved successfully");
                        this.clearSaving();
                        if (this.$route.path.includes('engineer/')) {
                            this.$router.push({
                                name: "engineer.forms.edit",
                                params: {
                                    id: res.data.id
                                }
                            });
                        } else {
                            this.$router.push({
                                name: "office.forms.edit",
                                params: {
                                    id: res.data.id
                                }
                            });
                        }
                    },
                    err => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    }
                );
            }
        },

        deleteForm: function () {
            if (confirm("Are you sure you want to delete this form?")) {
                FormService.delete(this.form).then(
                    () => {
                        this.setInfoMessage("Form successfully deleted");
                        this.$router.push({name: 'office.forms.search'});
                    },
                    () => {
                        this.setErrorMessage("Could not delete this form");
                    }
                )
            }
        },

        onJobLoadedFromRoute() {
            this.siteChanged();
            this.getJobs();
        },

        getJobs: function () {
            if (!this.form.customer_id) {
                this.jobs = [];
                this.form.job_id = null;
                return;
            }
            JobService.search({
                "filter[customer_id]": this.form.customer_id,
            }).then(
                (res) => {
                    this.jobs = res.data.data || [];
                },
                (err) => {
                    this.setErrorMessage("Could not load customer jobs");
                    this.jobs = [];
                }
            );
        },

        calcBattery: function () {
            const q = parseFloat(this.form.battery_q || '0');
            const a = parseFloat(this.form.battery_a || '0');
            const standbyHours = parseFloat(this.form.battery_standby_hours || '0');
            const alarmHours = parseFloat(this.form.battery_alarm_hours || '0');

            const standbyCapacity = q * standbyHours;
            const alarmCapacity = a * alarmHours * 1.75;
            const standbyPlusAlarm = standbyCapacity + alarmCapacity;
            const batterySize = (standbyPlusAlarm / 1000 * 1.25);

            this.form.battery_standby_capacity = standbyCapacity;
            this.form.battery_alarm_capacity = alarmCapacity;
            this.form.battery_standby_and_alarm = standbyPlusAlarm;
            this.form.battery_size = batterySize.toFixed(3);
            this.$forceUpdate();
        }
    }
}
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/forms/FireAlarmCommissioningComponent.vue